import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import LinearProgress from '@mui/material/LinearProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Alert from '@mui/material/Alert'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useActionAuditQuery } from './useActionAuditQuery'
import { ENTITY_TYPES, getActionsForEntityType } from './actionAuditConstants'

/**
 * Port of `app/actionAudit/action-audit.html` +
 * `app/actionAudit/actionAuditController.js` (AngularJS `actionAuditController`)
 * to React/MUI, connected to the `workspace.actionAudit` route
 * (`/workspace/action-audit`, `AppRoutes.jsx`).
 *
 * Field-by-field mapping from the legacy template:
 * - `namespace` / `search` free-text inputs -> two `TextField`s (unchanged).
 * - `dateFrom` / `dateTo` (legacy `<input type="date">` driven by a
 *   `pickadate` jQuery plugin bolted on in an inline `<script>`, formatting
 *   as `dd/mm/yyyy` text) -> two native MUI `TextField type="date"`. MUI has
 *   no bundled date-picker (`@mui/x-date-pickers` is not a project
 *   dependency and no calendar/date-picker widget is used by any other
 *   already-ported module — see `client/package.json`), so this uses the
 *   browser's native date input, same as the legacy `<input type="date">`
 *   element actually was underneath the jQuery enhancement. Native date
 *   inputs give `YYYY-MM-DD` values; `_buildParams`'s
 *   `moment($scope.dateFrom, "DD/MM/YYYY").valueOf()` is replicated with
 *   `new Date(value).getTime()`, which parses `YYYY-MM-DD` natively without
 *   introducing `moment` as a new client dependency (it is not installed in
 *   `client/package.json` and no other ported module has added it).
 * - `type` (`<select>` of `entityTypes`) -> `TextField select` populated
 *   from `ENTITY_TYPES` (`actionAuditConstants.js`), `onChange` calling
 *   `onChangeEntityType()`'s port (`getActionsForEntityType`) to refresh the
 *   dependent "Ações" options, exactly like the legacy `ng-change`.
 * - `logEntityAction` (`<select>` of `actions`) -> `TextField select`
 *   populated from the current entity's `getActionsForEntityType(type)`.
 * - "Filtrar" button -> `filter()`'s port: resets the list and triggers
 *   `useActionAuditQuery().search(...)` with the built params.
 * - Results table (`Data`/`Entidade`/`Tratamento`/`Usuário`/`Detalhes`) ->
 *   MUI `Table`, same 5 columns, same empty-state and loading-state rows.
 * - "Detalhes" eye icon + `data-modal` (`openModalDetails`/`cancel`) -> MUI
 *   `Dialog` showing `audit.data` (kept as the raw JSON blob, same as the
 *   legacy `<p>{{details}}</p>` interpolation of whatever shape `audit.data`
 *   is — this screen has never known/cared about that shape's structure).
 *
 * Not ported: the inline `pickadate` initialization script (superseded by
 * the native date input above) and `_updateMaterialSelect()` /
 * `$('select').material_select()` (Materialize-specific `<select>`
 * enhancement, meaningless once the `<select>` itself is replaced by MUI's
 * own `TextField select`).
 */

function toEpochMillis(dateInputValue, { endOfDay = false } = {}) {
  if (!dateInputValue) return undefined
  const suffix = endOfDay ? 'T23:59:59.999' : 'T00:00:00.000'
  const parsed = new Date(`${dateInputValue}${suffix}`).getTime()
  return Number.isNaN(parsed) ? undefined : parsed
}

const INITIAL_FILTERS = {
  namespace: '',
  search: '',
  dateFrom: '',
  dateTo: '',
  type: '',
  logEntityAction: '',
}

function ActionAuditPage() {
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [details, setDetails] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)

  const { audits, isLoading, isLoadingMore, isError, hasMore, search, loadMore } =
    useActionAuditQuery()

  const availableActions = useMemo(() => getActionsForEntityType(filters.type), [filters.type])

  function handleFieldChange(field) {
    return (event) => {
      const { value } = event.target
      setFilters((current) => ({ ...current, [field]: value }))
    }
  }

  // Port of `onChangeEntityType()`: changing the entity resets the
  // dependent action filter, since the previous selection may no longer be
  // valid for the newly selected entity.
  function handleEntityTypeChange(event) {
    const { value } = event.target
    setFilters((current) => ({ ...current, type: value, logEntityAction: '' }))
  }

  // Port of `$scope.filter()`.
  function handleFilter() {
    setHasSearched(true)
    search({
      namespace: filters.namespace.trim(),
      logEntityAction: filters.logEntityAction,
      type: filters.type,
      term: filters.search.trim(),
      fromDate: toEpochMillis(filters.dateFrom),
      toDate: toEpochMillis(filters.dateTo, { endOfDay: true }),
    })
  }

  function handleOpenDetails(data) {
    setDetails(data)
  }

  function handleCloseDetails() {
    setDetails(null)
  }

  const showEmptyState = hasSearched && !isLoading && audits.length === 0 && !hasMore
  const showResults = hasSearched && !isLoading && audits.length > 0

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Auditorias de ações
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Stack spacing={2}>
          <TextField
            id="namespace"
            label="Digite um namespace"
            fullWidth
            value={filters.namespace}
            onChange={handleFieldChange('namespace')}
          />

          <TextField
            id="search"
            label="Digite um termo de pesquisa"
            fullWidth
            value={filters.search}
            onChange={handleFieldChange('search')}
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
            <TextField
              id="dateFrom"
              label="De"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={filters.dateFrom}
              onChange={handleFieldChange('dateFrom')}
              sx={{ minWidth: 180 }}
            />

            <Typography>até</Typography>

            <TextField
              id="dateTo"
              label="Até"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={filters.dateTo}
              onChange={handleFieldChange('dateTo')}
              sx={{ minWidth: 180 }}
            />

            <TextField
              id="type"
              select
              label="Entidade"
              value={filters.type}
              onChange={handleEntityTypeChange}
              sx={{ minWidth: 220 }}
              fullWidth
            >
              {ENTITY_TYPES.map((entityType) => (
                <MenuItem key={entityType.value} value={entityType.value}>
                  {entityType.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              id="logEntityAction"
              select
              label="Ações"
              value={filters.logEntityAction}
              onChange={handleFieldChange('logEntityAction')}
              sx={{ minWidth: 220 }}
              fullWidth
              disabled={availableActions.length === 0}
            >
              {availableActions.map((action) => (
                <MenuItem key={action.value} value={action.value}>
                  {action.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box>
            <Button
              variant="contained"
              onClick={handleFilter}
              disabled={isLoading || isLoadingMore}
            >
              Filtrar
            </Button>
          </Box>
        </Stack>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {audits.length > 0
            ? 'Erro ao carregar mais resultados. Os resultados já carregados continuam disponíveis.'
            : 'Erro na requisição'}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Entidade</TableCell>
              <TableCell>Tratamento</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} sx={{ p: 0 }}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}

            {showEmptyState && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum registro de ação encontrado
                </TableCell>
              </TableRow>
            )}

            {showResults &&
              audits.map((audit, index) => (
                <TableRow key={`${audit.date}-${audit.userName}-${index}`}>
                  <TableCell>{formatDate(audit.date)}</TableCell>
                  <TableCell>{audit.entityType}</TableCell>
                  <TableCell>{audit.did}</TableCell>
                  <TableCell>{audit.userName}</TableCell>
                  <TableCell>
                    <Tooltip title="Visualizar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDetails(audit.data)}
                        aria-label="Visualizar"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {hasMore && (
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={loadMore} disabled={isLoadingMore}>
            {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
          </Button>
        </Box>
      )}

      <Dialog open={details !== null} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Dados da ação</DialogTitle>
        <DialogContent>
          <Typography component="p" sx={{ wordBreak: 'break-word' }}>
            {typeof details === 'object' ? JSON.stringify(details) : details}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Port of the legacy `_formatDate`: `moment(dateToFormat).format('DD/MM/YYYY')`.
// The audit log's `date` field is a timestamp (epoch millis or ISO string
// depending on the backend log entry), same as what `moment(...)` accepted
// without an explicit input format there — `Date` parses both natively.
function formatDate(dateToFormat) {
  const parsed = new Date(dateToFormat)
  if (Number.isNaN(parsed.getTime())) return ''

  const day = String(parsed.getDate()).padStart(2, '0')
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const year = parsed.getFullYear()
  return `${day}/${month}/${year}`
}

export default ActionAuditPage
